import { useCallback, useEffect, useRef } from 'react';

import { findChatScrollContainer, isScrollAtBottom, scrollContainerToBottom } from './chatScroll';

/**
 * Keeps chat pinned to the bottom while streaming unless the user scrolls away.
 * Auto-scroll resumes when the user returns to the bottom or sends a new message.
 */
const useChatAutoScroll = ({ bottomRef, conversation, userMessageCount }) => {
    const isAutoScrollEnabledRef = useRef(true);
    const programmaticScrollRef = useRef(false);
    const scrollContainerRef = useRef(null);
    const scrollRafRef = useRef(null);
    const userMessageCountRef = useRef(userMessageCount);

    const scrollToBottom = useCallback(
        ({ force = false } = {}) => {
            if (!force && !isAutoScrollEnabledRef.current) return;

            if (scrollRafRef.current) {
                cancelAnimationFrame(scrollRafRef.current);
            }

            scrollRafRef.current = requestAnimationFrame(() => {
                const container = findChatScrollContainer(bottomRef.current);
                scrollContainerRef.current = container;

                if (!container) return;

                programmaticScrollRef.current = true;
                scrollContainerToBottom(container);

                requestAnimationFrame(() => {
                    programmaticScrollRef.current = false;
                });
            });
        },
        [bottomRef],
    );

    useEffect(() => {
        const container = findChatScrollContainer(bottomRef.current);
        scrollContainerRef.current = container;

        if (!container) return undefined;

        const handleScroll = () => {
            if (programmaticScrollRef.current) return;

            isAutoScrollEnabledRef.current = isScrollAtBottom(container);
        };

        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [bottomRef, conversation, userMessageCount]);

    useEffect(() => {
        const previousCount = userMessageCountRef.current;

        if (userMessageCount < previousCount) {
            isAutoScrollEnabledRef.current = true;
        } else if (userMessageCount > previousCount) {
            isAutoScrollEnabledRef.current = true;
            scrollToBottom({ force: true });
        }

        userMessageCountRef.current = userMessageCount;
    }, [scrollToBottom, userMessageCount]);

    useEffect(() => {
        return () => {
            if (scrollRafRef.current) {
                cancelAnimationFrame(scrollRafRef.current);
            }
        };
    }, []);

    return scrollToBottom;
};

export default useChatAutoScroll;
